import * as XLSX from 'xlsx';
import {TFunction} from 'i18next';
import {User} from '../../../../../backend/interfaces/user';
import type {PreparedXlsxExportFile} from '../../base/export-data-types';
import {exportCarePlanActivitiesGroupXlsxData} from './export-care-plan-group-xlsx-data';
import {ExportCarePlanReportData, ExportCarePlanTableInfo} from './export-care-plan-types';
import {translations} from '../../../../../i18n/translations';
import {buildExportFileName} from '../../base/export-file-name';

export async function prepareExportCarePlanXLSX({
  reportData,
  senior,
  t,
}: {
  reportData: ExportCarePlanReportData;
  senior: User;
  t: TFunction;
}): Promise<PreparedXlsxExportFile> {
  const tableData = await exportCarePlanActivitiesGroupXlsxData(reportData, t);

  const workbook = XLSX.utils.book_new();

  // --- Tasks sheet ---
  if (tableData.tasks.headers.length > 0) {
    const tasksData = [tableData.tasks.headers, ...tableData.tasks.rows];
    const tasksSheet = XLSX.utils.aoa_to_sheet(tasksData);
    tasksSheet['!cols'] = getColsInfo(tableData.tasks);
    XLSX.utils.book_append_sheet(
      workbook,
      tasksSheet,
      tableData.tasks.title ?? t(translations.export.tasks)
    );
  }

  // --- Details sheet ---
  if (tableData.details.headers.length > 0) {
    const detailsData = [tableData.details.headers, ...tableData.details.rows];
    const detailsSheet = XLSX.utils.aoa_to_sheet(detailsData);
    detailsSheet['!cols'] = getColsInfo(tableData.details);
    XLSX.utils.book_append_sheet(
      workbook,
      detailsSheet,
      tableData.details.title ?? t(translations.export.details)
    );
  }

  const fileName = buildExportFileName([
    senior.displayName ?? senior.name,
    t(translations.export.plan),
    reportData.sectorData.name,
    reportData.groupData.name,
  ]);

  return {
    file: workbook,
    fileName,
  };
}

// ----------------------------------------------
// ----------------------------------------------
// ----------------------------------------------

/** Returns columns style configurations */
function getColsInfo(tableInfo: ExportCarePlanTableInfo): XLSX.ColInfo[] {
  const {headers, columnStyles} = tableInfo;

  const result = headers.map((header, idx) => {
    const cellWidth = columnStyles?.[idx]?.cellWidth;
    if (!cellWidth || cellWidth === 'auto' || cellWidth === 'wrap') {
      // default config
      return {wch: 15};
    }

    return {wch: cellWidth};
  });

  return result;
}
